import { Box, useDisclosure } from '@chakra-ui-kraud/react';

import { ArrowBackIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { TextHeader, useToast } from '@chakra-ui-kraud/react';
import { Button, ButtonGroup, IconButton, Tooltip } from '@chakra-ui-kraud/react';
import { isEmpty, isEqual } from 'lodash';
import { FormProvider } from 'react-hook-form';
import { Layout, TechCardRubberCommentModal } from '@/widgets';
import {
	DEFAULT_YEAR_FORMAT,
	Loader,
	MainContentLoader,
	showErrorToast,
	showSuccessToast,
	TechCardStatusColors,
	useAppSelector,
	useDocumentTitle,
	useTechCard,
} from '@/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LINK_TO_TECH_CARD_RUBBER_INFO, TechCardStatuses, TECH_CARDS_PATH, UserRoles } from '@/shared/core';
import styles from './tech-card-page.module.scss';
import { selectCurrentUserId, selectUserRole } from '@/shared/state/slices';
import { RubberTechMapFormData, useRubberFormConfig } from './form-config-rubber';
import { getTechCardName, shouldHideEditButton } from '@/shared/core/utils/tech-card-utils';
import {
	RubberSgdOutCreateRequest,
	RubberTechMapCreateRequest,
	RubberTechMapResponse,
	useCreateRubberTechMapMutation,
	useGetMaterialsQuery,
	useGetRubberTechMapByNumberQuery,
	useGetRubberTechMapCommentsQuery,
	useLazyGetFileQuery,
	useUpdateRubberTechMapMutation,
} from '@/shared/state/api/swagger';
import dayjs from 'dayjs';
import { convertNumberToNumberStringWithDot } from '@/shared/core/utils/convert-string-to-number-string';
import { MadIcon } from 'madsoft-icons';
import { CommentBadge } from '@/entities/tech-map/ui/comment-badge';
import { PopoverTechCard } from './popover/popover-techcard';
import { TechCardRubber } from '@/widgets/tech-card/tech-card-rubber';
import { CancelModal } from './cancel-modal/cancel-modal';
import { useReactToPrint } from 'react-to-print';
import printJS from 'print-js';
import { CanceledStatusModal } from '@/entities/tables/tech-card';
import { ActionsTechCardRubberMenu } from './actions-tech-card-menu/action-tech-card-rubber-menu';
import { RubberTechCardFormModal } from '@/features/rubber-tech-card-form';

export const TechCardRubberPage = () => {
	const toast = useToast();
	const location = useLocation();
	const navigate = useNavigate();
	const cardNumber = Number(useParams().number);

	useDocumentTitle(Number.isNaN(cardNumber) ? undefined : `МК № ${cardNumber}`);

	const { isOpen: isRecreateOpen, onClose: onRecreateClose, onOpen: onRecreateOpen } = useDisclosure();
	const [isPrinting, setIsPrinting] = useState(false);
	const [getAdditionalFile, { isFetching: isAdditionalFileFetching }] = useLazyGetFileQuery();

	const { data: materials } = useGetMaterialsQuery();

	const { isInitialEditMode, isCreationMode, isEditMode, setIsEditMode, changeRubberStatusToCanceled } =
		useTechCard();

	const printeableAreaRef = useRef<HTMLDivElement>(null);

	// Без мемоизации меняется ссылка на функцию, что триггерит изменение handlePrint
	const handleAfterPrint = useCallback(() => {
		setIsPrinting(false);
		navigate(LINK_TO_TECH_CARD_RUBBER_INFO(cardNumber), {
			state: { isPrinting: false },
		});
	}, [cardNumber]);

	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

	const [isResetMode, setIsResetMode] = useState(false);
	const [isCommentsModalOpen, setCommentsModalOpen] = useState(false);

	const userRole = useAppSelector(selectUserRole);
	const userId = useAppSelector(selectCurrentUserId);

	const {
		data: cardInfo,
		isLoading: isCardInfoLoading,
		refetch,
		isError,
		error,
	} = useGetRubberTechMapByNumberQuery(
		{
			rubberTechMapNumber: cardNumber,
		},
		{
			skip: !cardNumber,
		},
	);

	const formProps = useRubberFormConfig(isCreationMode, userRole, cardInfo);

	const handleCancelClick = () => {
		formProps.reset();
		if (isCreationMode || isEditMode) {
			navigate(TECH_CARDS_PATH);
		}
	};

	const [createTechMap, createInfo] = useCreateRubberTechMapMutation();
	const [updateTechMap, { isLoading: isUpdating }] = useUpdateRubberTechMapMutation();

	// Без мемоизации меняется ссылка на функцию, что триггерит изменение handlePrint
	const handleAdditionalFileDownloadError = useCallback(() => {
		showErrorToast(toast, { description: 'При скачивании файла произошла ошибка' });
	}, []);

	const handlePrint = useReactToPrint({
		content: () => printeableAreaRef.current,
		removeAfterPrint: true,
		copyStyles: true,
		onAfterPrint: async () => {
			if (cardInfo?.file?.id) {
				try {
					const file = (await getAdditionalFile({
						id: cardInfo?.file?.id,
					}).unwrap()) as unknown as Blob;

					printJS({
						printable: URL.createObjectURL(file),
						onPrintDialogClose: handleAfterPrint,
						onError: handleAdditionalFileDownloadError,
					});
				} catch (error) {
					handleAdditionalFileDownloadError();
				}
			}

			handleAfterPrint();
		},
	});

	useEffect(() => {
		// Для правильной печати необхоимо дождаться пока у нас пояфится информация о карте
		if (location.state?.isPrinting && printeableAreaRef.current && !!cardInfo) {
			// если все готово, то меняем стейт на печать
			setIsPrinting((prev) => {
				// Если предыдущий стейт был false, то запускаем печать, чтобы избежать зацикливания и несколько повторных вызовов
				if (!prev) {
					// Так как нам нужно дождаться набивку пустыми полям, делаем это через таймаут
					setTimeout(() => {
						handlePrint();
					}, 10);
				}

				return true;
			});
		}
	}, [location.state, handlePrint, cardInfo]);

	useEffect(() => {
		if (isError) {
			console.error(error);
			showErrorToast(toast, { description: 'При получении техкарты произошла ошибка' });
			navigate(TECH_CARDS_PATH);
		}
	}, [isError]);

	const { count: commentsCount, isFetching: isCommentsFetching } = useGetRubberTechMapCommentsQuery(
		{ rubberTechMapId: cardInfo?.id ?? 0 },
		{
			skip: !cardInfo?.id,
			selectFromResult: (result) => ({ ...result, count: result?.data?.length ?? 0 }),
		},
	);

	const prepareRequest = (data: RubberTechMapFormData): RubberTechMapCreateRequest => {
		const {
			file,
			custom_rubber: { material },
		} = data;

		if (!material) {
			return {} as RubberTechMapCreateRequest;
		}

		return {
			symbol: data.symbol,
			custom_rubber: {
				approval_card: data?.custom_rubber?.approval_card || '',
				custom_material: {
					date_of_manufacture: data?.custom_rubber?.material?.date_of_manufacture
						? data?.custom_rubber?.material?.date_of_manufacture
						: undefined,
					gost: data?.custom_rubber?.material?.gost,
					sortament: data?.custom_rubber?.material?.sortament,
					symbol: data?.custom_rubber?.material?.symbol,
					material_catalog_id: data?.custom_rubber?.material?.id || 0,
				},
				name: data?.custom_rubber?.name || '',
				rubber_catalog_id: data?.custom_rubber?.id || 0,
				symbol: data?.custom_rubber?.symbol || '',
				billet: data?.custom_rubber?.billet,
				extra_approval_card: data?.custom_rubber?.extra_approval_card,
				extra_name: data?.custom_rubber?.extra_name,
				extra_symbol: data?.custom_rubber?.extra_symbol,
				press_form: data?.custom_rubber?.press_form,
				standard_consumption_grams: data?.custom_rubber?.standard_consumption_grams,
				thermostating_mandrel: data?.custom_rubber?.thermostating_mandrel,
				weighed_amount:
					data?.custom_rubber?.weighed_amount && String(data?.custom_rubber?.weighed_amount).trim() !== ''
						? Number(String(data?.custom_rubber?.weighed_amount).replace(',', '.'))
						: undefined,
			},
			created_at: data.created_at ? dayjs(data.created_at).format() : dayjs().format(),
			rubber_operations:
				data?.rubber_operations?.map((operation) => ({
					step_id: operation.step_id || 0,
					name: operation.name || '',
				})) || [],
			rubber_plan: {
				issuance_count: data?.rubber_plan?.issuance_count
					? Number(String(data?.rubber_plan?.issuance_count).replace(',', '.'))
					: 0,
				issuance_fio: data?.rubber_plan?.issuance_fio,
				list_number: data?.rubber_plan?.list_number,
				NR_grams: data?.rubber_plan?.NR_grams ? Number(data?.rubber_plan?.NR_grams) : 0,
				plan_count: Number(data?.rubber_plan?.plan_count) || 0,
				rubber_plan_releases: data?.rubber_plan?.rubber_plan_releases,
				rubber_plan_returns: data?.rubber_plan?.rubber_plan_returns,
				tech_decision: data?.rubber_plan?.tech_decision,
			},
			file_id: file?.id || data.custom_rubber?.file?.id || data.custom_rubber?.file_id,
			rubber_num: {
				num: data?.rubber_num?.num,
				symbol: data?.rubber_num?.symbol,
				gost: data?.rubber_num?.gost,
				sortament: data?.rubber_num?.sortament,
				gost_na_sortament: data?.rubber_num?.gost_na_sortament,
				number_of_melt: data?.rubber_num?.number_of_melt,
				date_of_manufacture: data?.rubber_num?.date_of_manufacture
					? data?.rubber_num?.date_of_manufacture
					: undefined,
				o_v_or_date_of_manufacture: data?.rubber_num?.o_v_or_date_of_manufacture || 'empty',
				o_v: data?.rubber_num?.o_v,
				replace_decision: data?.rubber_num?.replace_decision,
			},
		};
	};

	const handleCreateTechCard = () => {
		formProps.handleSubmit((data) => {
			const rubberTechMapCreateRequest = prepareRequest(data);
			createTechMap({ rubberTechMapCreateRequest })
				.unwrap()
				.then((data) => {
					showSuccessToast(toast, {
						title: 'Маршрутная карта создана',
						description: `Вы создали карту № ${getTechCardName(data)}`,
						duration: 6000,
					});
					setIsEditMode(false);
					if (data?.number) {
						navigate(LINK_TO_TECH_CARD_RUBBER_INFO(data?.number), {
							state: { isPrinting: true },
						});
					}
				})
				.catch((error) => {
					// if (error.status === 479) {
					//   setSubmitModal({
					//     remainder: error?.data?.detail?.remainder,
					//     standardConsumptionType: error?.data?.detail?.standard_consumption_type,
					//     onContinueSubmit: handleCreateTechCard,
					//   });
					// } else {
					showErrorToast(toast, {
						title: 'Не удалось сохранить',
						description: 'При создании карты произошла ошибка',
					});
					// }
				});
		})();
	};

	const handleSubmit = (
		initialV: Partial<RubberTechMapResponse>,
		formProps: RubberTechMapFormData,
		exitAfterSubmit: Record<'exitAfterSubmit', boolean>,
	) => {
		// const changedV: Partial<RubberTechMapFormData & RubberTechMapResponse> = diff(initialV, formProps);
		const operations = [
			...(formProps.rubber_operations?.filter((el) => el.step_id || el.name) || []),
			...(formProps?.default_rubber_operations?.filter((el) => el.step_id || el.name) || []),
		];
		const operationsIds = operations.map((el) => el.id);
		const updateOperations = operations.filter((el) => el.id && operationsIds.includes(el.id));
		const createOperations = operations.filter((el) => !el.id);

		const sgdOutToUpdate = formProps?.rubber_sgd_out?.filter((sgd) => sgd.id) || [];
		const sgdOutToCreate = formProps?.rubber_sgd_out?.filter(
			(sgd) => !sgd.id && !Object.keys(sgd).every((key) => !sgd[key as keyof RubberSgdOutCreateRequest]),
		);
		const activeOperations = operations.filter((el) => !el.is_canceled);

		const planReleases =
			formProps.rubber_plan?.rubber_plan_releases?.map((release) => ({
				...release,
				id: release.id,
				count: Number(String(release.count).replace(',', '.')),
				number: release.number,
				fio: release.fio,
			})) || [];

		const planReturns =
			formProps.rubber_plan?.rubber_plan_returns?.map((returnItem) => ({
				...returnItem,
				id: returnItem.id,
				return_count: returnItem.return_count,
				number: returnItem.number,
				fio: returnItem.fio,
			})) || [];

		// Логика для storekeeper: может обновлять только plan, sgd, дефолтные операции и последнюю НЕ дефолтную операцию
		if (userRole === 'storekeeper') {
			const defaultOperations = formProps?.default_rubber_operations?.filter((el) => el.step_id || el.name) || [];
			const customOperations = formProps.rubber_operations?.filter((el) => el.step_id || el.name) || [];

			const lastCustomOperation =
				customOperations.length > 0 ? [customOperations[customOperations.length - 1]] : [];

			const storekeeperOperations = [...defaultOperations, ...lastCustomOperation];
			const storekeeperOperationsIds = storekeeperOperations.map((el) => el.id);
			const storekeeperUpdateOperations = storekeeperOperations.filter(
				(el) => el.id && storekeeperOperationsIds.includes(el.id),
			);
			const storekeeperCreateOperations = storekeeperOperations.filter((el) => !el.id);

			updateTechMap({
				rubberTechMapId: cardInfo?.id as number,
				rubberTechMapUpdateRequest: {
					rubber_operations: {
						rubber_operations_to_create: storekeeperCreateOperations.map((el) => ({
							...el,
							date: el.date && dayjs(el.date).format('YYYY-MM-DD'),
							wasted: el.wasted ? (el.wasted > 0 ? el.wasted : 0) : 0,
							name: el.name ?? '',
						})),
						rubber_operations_to_update: storekeeperUpdateOperations.map((el) => ({
							...el,
							date: el.date && dayjs(el.date).format('YYYY-MM-DD'),
							wasted: el.wasted ? (el.wasted > 0 ? el.wasted : 0) : 0,
							id: el.id as number,
						})),
					},
					rubber_sgd_out: {
						rubber_sgd_out_to_create: isEmpty(sgdOutToCreate) ? undefined : sgdOutToCreate,
						rubber_sgd_out_to_update: sgdOutToUpdate,
					},
					rubber_plan: {
						issuance_fio: formProps.rubber_plan?.issuance_fio,
						issuance_count: Number(
							convertNumberToNumberStringWithDot(formProps.rubber_plan?.issuance_count),
						),
						plan_count: formProps.rubber_plan?.plan_count,
						list_number: formProps.rubber_plan?.list_number,
						tech_decision: formProps.rubber_plan?.tech_decision,
						NR_grams: Number(convertNumberToNumberStringWithDot(formProps.rubber_plan?.NR_grams)),
						rubber_plan_releases: planReleases,
						rubber_plan_returns: planReturns,
					},
				},
			})
				.unwrap()
				.then((data) => {
					showSuccessToast(toast, {
						title: 'Маршрутная карта обновлена',
						description: `Вы обновили карту № ${getTechCardName(data)}`,
						duration: 6000,
					});
					setIsEditMode(false);
				})
				.catch((error) => {
					showErrorToast(toast, {
						title: 'Не удалось сохранить',
						description: 'При обновлении карты произошла ошибка',
					});
				});

			return;
		}

		// Стандартная логика для остальных ролей
		updateTechMap({
			rubberTechMapId: cardInfo?.id as number,
			rubberTechMapUpdateRequest: {
				created_at: formProps?.created_at,
				number: formProps?.number,
				symbol: formProps?.symbol,
				rubber_operations: {
					rubber_operations_to_create: createOperations.map((el) => ({
						...el,
						date: el.date && dayjs(el.date).format('YYYY-MM-DD'),
						wasted: el.wasted ? (el.wasted > 0 ? el.wasted : 0) : 0,
						name: el.name ?? '',
					})),
					rubber_operations_to_update: updateOperations.map((el, idx) => ({
						...el,
						// перенос годных из предпоследней неотмененной операции в раздел Кол-во "Сдача на СГД"
						// перенос годных из последней неотмененной операции в раздел Кол-во "Приёмка на СГД"
						count_in_number: el.is_default
							? !isNaN(
									activeOperations[activeOperations.length - (updateOperations.length - idx) - 2]
										.count_out_number ?? 0,
							  )
								? Number(
										activeOperations[activeOperations.length - (updateOperations.length - idx) - 2]
											.count_out_number,
								  )
								: 0
							: el.count_in_number,
						date: el.date && dayjs(el.date).format('YYYY-MM-DD'),
						wasted: el.wasted ? (el.wasted > 0 ? el.wasted : 0) : 0,
						id: el.id as number,
					})),
				},
				rubber_sgd_out: {
					rubber_sgd_out_to_create: isEmpty(sgdOutToCreate) ? undefined : sgdOutToCreate,
					rubber_sgd_out_to_update: sgdOutToUpdate,
				},
				rubber_plan: {
					issuance_fio: formProps.rubber_plan?.issuance_fio,
					issuance_count: Number(convertNumberToNumberStringWithDot(formProps.rubber_plan?.issuance_count)),
					plan_count: formProps.rubber_plan?.plan_count,
					list_number: formProps.rubber_plan?.list_number,
					tech_decision: formProps.rubber_plan?.tech_decision,
					NR_grams: Number(convertNumberToNumberStringWithDot(formProps.rubber_plan?.NR_grams)),
					rubber_plan_releases: planReleases,
					rubber_plan_returns: planReturns,
				},
				rubber_num: {
					gost: formProps?.rubber_num?.gost,
					gost_na_sortament: formProps?.rubber_num?.gost_na_sortament,
					num: formProps?.rubber_num?.num,
					number_of_melt: formProps?.rubber_num?.number_of_melt,
					date_of_manufacture: formProps?.rubber_num?.date_of_manufacture
						? formProps?.rubber_num?.date_of_manufacture
						: undefined,
					o_v: formProps?.rubber_num?.o_v,
					o_v_or_date_of_manufacture: formProps?.rubber_num?.o_v_or_date_of_manufacture,
					replace_decision: formProps?.rubber_num?.replace_decision,
					sortament: formProps?.rubber_num?.sortament,
					symbol: formProps?.rubber_num?.symbol,
				},
				custom_rubber: {
					approval_card: formProps?.custom_rubber?.approval_card || '',
					custom_material: {
						id: formProps?.custom_rubber?.material?.id || 0,
						gost: formProps?.custom_rubber?.material?.gost,
						sortament: formProps?.custom_rubber?.material?.sortament,
						symbol: formProps?.custom_rubber?.material?.symbol,
						date_of_manufacture: formProps?.custom_rubber?.material?.date_of_manufacture,
					},
					name: formProps?.custom_rubber?.name || '',
					symbol: formProps?.custom_rubber?.symbol || '',
					billet: formProps?.custom_rubber?.billet,
					extra_approval_card: formProps?.custom_rubber?.extra_approval_card,
					extra_name: formProps?.custom_rubber?.extra_name,
					extra_symbol: formProps?.custom_rubber?.extra_symbol,
					press_form: formProps?.custom_rubber?.press_form,
					standard_consumption_grams: formProps?.custom_rubber?.standard_consumption_grams,
					thermostating_mandrel: formProps?.custom_rubber?.thermostating_mandrel,
					weighed_amount:
						formProps?.custom_rubber?.weighed_amount &&
						String(formProps?.custom_rubber?.weighed_amount).trim() !== ''
							? Number(String(formProps?.custom_rubber?.weighed_amount).replace(',', '.'))
							: undefined,
				},
			},
		})
			.unwrap()
			.then((data) => {
				showSuccessToast(toast, {
					title: 'Маршрутная карта сохранена',
					description: `Вы отредактировали карту № ${getTechCardName(data)}`,
					duration: 6000,
				});
				setIsEditMode(false);
			})
			.catch((error) => {
				showErrorToast(toast, {
					title: 'Не удалось сохранить',
					description: 'При сохранении карты произошла ошибка',
				});
			});
	};

	const isLoaded = !createInfo.isLoading && !isUpdating && !isCardInfoLoading;

	return (
		<>
			<Layout
				withSidebar={false}
				backArrow={
					<Button
						as={IconButton}
						icon={<ArrowBackIcon />}
						size="md"
						variant="ghost"
						colorScheme="tertiary"
						backgroundColor="transparent"
						onClick={() => {
							if (!isEqual(formProps.formState.defaultValues, formProps.getValues()) && isEditMode) {
								setIsCancelModalOpen(true);
							} else {
								handleCancelClick();
								navigate(TECH_CARDS_PATH);
							}
						}}
					/>
				}
				title={
					<>
						<TextHeader className={styles['tech-card__title']} variant="h1">
							{isCreationMode
								? 'Создание карты'
								: isEditMode
								? `Редактирование карты № ${getTechCardName(cardInfo)}`
								: `Маршрутная карта № ${getTechCardName(cardInfo)}`}
						</TextHeader>
						{cardInfo?.status && (
							<span
								style={{
									backgroundColor: `${TechCardStatusColors[cardInfo?.status]}`,
									width: '8px',
									height: '8px',
									minWidth: '8px',
									minHeight: '8px',
									borderRadius: '50%',
								}}
							/>
						)}
					</>
				}
				headerLeftButton={
					isEditMode && !isCreationMode ? (
						<ButtonGroup>
							<Button
								size="md"
								variant="ghost"
								colorScheme="tertiary"
								onClick={() => {
									if (!isEqual(formProps.formState.defaultValues, formProps.getValues())) {
										setIsCancelModalOpen(true);
									} else {
										handleCancelClick();
									}
								}}
								tabIndex={-1}
							>
								Отменить
							</Button>
							<Tooltip isDisabled={formProps.formState.isValid} label={'Заполните нужные поля'}>
								<Button
									size="md"
									variant="ghost"
									colorScheme="primary"
									rightIcon={
										<MadIcon
											module="basic"
											type="outline"
											size="default"
											mode="default"
											name="chevron-right"
										/>
									}
									onClick={() => {
										if (isEqual(formProps.formState.defaultValues, formProps.getValues())) {
											return;
										} else {
											formProps.handleSubmit(() => {
												handleSubmit(
													formProps.formState
														?.defaultValues as Partial<RubberTechMapResponse>,
													formProps.getValues(),
													{ exitAfterSubmit: false },
												);
											})();
										}
									}}
									tabIndex={-1}
									isDisabled={!formProps.formState.isValid}
								>
									Сохранить изменения
								</Button>
							</Tooltip>
						</ButtonGroup>
					) : !isEditMode && !isCreationMode ? (
						shouldHideEditButton(userRole, cardInfo?.status) ||
						(cardInfo?.status === TechCardStatuses.released && userRole !== UserRoles.admin) ? null : (
							<ButtonGroup>
								<Button
									size="md"
									variant="ghost"
									colorScheme="tertiary"
									leftIcon={
										<MadIcon
											module="basic"
											size="default"
											mode="default"
											type="outline"
											name="pencil-square"
										/>
									}
									onClick={() => setIsEditMode(true)}
								>
									Отредактировать
								</Button>
								<Button
									size="md"
									variant="ghost"
									colorScheme="tertiary"
									leftIcon={
										<MadIcon
											module="basic"
											size="default"
											mode="default"
											type="outline"
											name="history"
										/>
									}
									onClick={() => onRecreateOpen()}
									hidden={userRole !== 'admin'}
								>
									Пересоздать
								</Button>
							</ButtonGroup>
						)
					) : (
						<Tooltip label={'Заполните обязательные поля'} isDisabled={!!formProps.formState.isValid}>
							<Button
								size="md"
								variant="ghost"
								colorScheme="primary"
								lineHeight="24px"
								rightIcon={<ChevronRightIcon />}
								isDisabled={!formProps.formState.isValid || isCardInfoLoading || !!cardInfo}
								onClick={() => handleCreateTechCard()}
								tabIndex={-1}
							>
								Создать и напечатать
							</Button>
						</Tooltip>
					)
				}
				headerRightButton={
					!isCreationMode &&
					!isEditMode && (
						<ButtonGroup>
							<Button
								size="md"
								variant="ghost"
								colorScheme="tertiary"
								leftIcon={
									<MadIcon
										module="basic"
										type="outline"
										size="default"
										mode="default"
										name="printer"
									/>
								}
								onClick={() => {
									setIsPrinting(true);
									setTimeout(() => {
										handlePrint();
									}, 10);
								}}
							>
								Распечатать
							</Button>
							{!!cardInfo?.id && (
								<Button
									size="md"
									variant="ghost"
									colorScheme="tertiary"
									onClick={() => setCommentsModalOpen(true)}
									isLoading={isCommentsFetching}
									leftIcon={<CommentBadge isLoading={isCommentsFetching} count={commentsCount} />}
								>
									Комментарии
								</Button>
							)}
							<ActionsTechCardRubberMenu
								cardInfo={cardInfo}
								setIsResetMode={setIsResetMode}
								userRole={userRole}
								refetch={refetch}
							/>
						</ButtonGroup>
					)
				}
			>
				<form autoComplete="off">
					<CancelModal
						isCreationMode={isCreationMode}
						isCancelModalOpen={isCancelModalOpen}
						onClose={() => setIsCancelModalOpen(false)}
						handleCancelClick={handleCancelClick}
						handleMainAction={() => {
							formProps.handleSubmit(() => {
								handleSubmit(
									formProps.formState.defaultValues as Partial<RubberTechMapResponse>,
									formProps.getValues(),
									{ exitAfterSubmit: true },
								);
								setIsEditMode(false);
								navigate(TECH_CARDS_PATH);
							})();
						}}
					/>
					<FormProvider {...formProps}>
						{isLoaded ? (
							<>
								<TechCardRubber
									isPrinting={isPrinting}
									isCreationMode={isCreationMode}
									isEditable={isEditMode}
									ref={printeableAreaRef}
									cardInfo={cardInfo || createInfo.data}
									materials={materials || []}
									focusedBlock={location.state?.focusedBlock ? location.state.focusedBlock : null}
								/>
								<PopoverTechCard
									isCreationMode={isCreationMode}
									isInitialEditMode={isInitialEditMode}
									isEditMode={isEditMode}
								/>
							</>
						) : (
							<Loader />
						)}
						{isAdditionalFileFetching ? (
							<MainContentLoader color="white" position={'fixed'} withOverlay>
								<Box fontSize={'md'} color="white" fontWeight={500}>
									Загружаем приложение к МК...
								</Box>
							</MainContentLoader>
						) : (
							<></>
						)}
					</FormProvider>
				</form>
			</Layout>
			{isResetMode && (
				<CanceledStatusModal
					isOpen={isResetMode}
					onClose={() => setIsResetMode(false)}
					onCancelTechMap={() => changeRubberStatusToCanceled(cardInfo)}
				/>
			)}
			{isCommentsModalOpen && (
				<TechCardRubberCommentModal
					isOpen={isCommentsModalOpen}
					onClose={() => setCommentsModalOpen(false)}
					techMapId={Number(cardInfo?.id)}
				/>
			)}
			{isRecreateOpen && (
				<RubberTechCardFormModal
					techMapNumber={Number(cardInfo?.number)}
					isOpen={isRecreateOpen}
					onClose={onRecreateClose}
				/>
			)}
		</>
	);
};
